import * as THREE from 'three';
import { useEffect, useRef } from 'react';
import {
    clearLaserCanvas,
    clearLaserTrail,
    createEmptyLaserTrailState,
    drawLaserStroke,
    getLaserConfig,
    hideLaserCursor,
    LASER_CANVAS_STYLE,
    LASER_CURSOR_STYLE,
    positionLaserCursor,
    resetLaserPointerState,
    resizeLaserCanvas,
} from './sceneViewportUtils';

function useLaserOverlay({
    controlsRef,
    interactionMode,
    rendererDomElement,
}) {
    const activeMousePointerIdRef = useRef(null);
    const flushMousePointerInteractionRef = useRef(null);
    const interactionModeRef = useRef(interactionMode);
    const laserRotateModifierPressedRef = useRef(false);
    const laserCanvasRef = useRef(null);
    const laserCanvasContextRef = useRef(null);
    const laserCursorRef = useRef(null);
    const laserPointerStateRef = useRef({
        inside: false,
        isPressed: false,
        lastSampleAt: 0,
        lastSamplePosition: null,
        localX: 0,
        localY: 0,
    });
    const laserTrailRef = useRef(createEmptyLaserTrailState());
    const frameIdRef = useRef(null);
    const {
        fadeMs,
        sampleMinDistance,
        sampleMinIntervalMs,
    } = getLaserConfig();

    const syncControlsInteractionBindings = () => {
        const controls = controlsRef.current;

        if (!controls) {
            return;
        }

        if (interactionModeRef.current === 'laser') {
            controls.enabled = true;
            controls.mouseButtons.LEFT = laserRotateModifierPressedRef.current
                ? THREE.MOUSE.ROTATE
                : THREE.MOUSE.NONE;
            controls.mouseButtons.MIDDLE = THREE.MOUSE.DOLLY;
            controls.mouseButtons.RIGHT = THREE.MOUSE.PAN;
            return;
        }

        controls.enabled = true;
        controls.mouseButtons.LEFT = THREE.MOUSE.ROTATE;
        controls.mouseButtons.MIDDLE = THREE.MOUSE.DOLLY;
        controls.mouseButtons.RIGHT = THREE.MOUSE.PAN;
    };

    useEffect(() => {
        interactionModeRef.current = interactionMode;

        if (interactionMode === 'laser') {
            flushMousePointerInteractionRef.current?.();
        } else {
            resetLaserPointerState(laserPointerStateRef);
            clearLaserTrail(laserTrailRef, laserPointerStateRef);
            hideLaserCursor(laserCursorRef.current);
            clearLaserCanvas(laserCanvasContextRef, laserCanvasRef);
        }

        syncControlsInteractionBindings();

        if (rendererDomElement) {
            const targetRendererDomElement = rendererDomElement;
            targetRendererDomElement.style.cursor = interactionMode === 'laser' ? 'none' : '';
        }
    }, [interactionMode, rendererDomElement]);

    useEffect(() => {
        if (!rendererDomElement) {
            return undefined;
        }

        let resizeObserver = null;
        laserCanvasContextRef.current = resizeLaserCanvas(laserCanvasRef.current);

        function stopLaserTrail(options = {}) {
            const { fade = true } = options;
            const laserTrail = laserTrailRef.current;
            const { activeStroke } = laserTrail;

            laserPointerStateRef.current.isPressed = false;
            laserPointerStateRef.current.lastSampleAt = 0;
            laserPointerStateRef.current.lastSamplePosition = null;

            if (fade && activeStroke.length > 1) {
                laserTrail.fadingStrokes.push({
                    fadeStartedAt: window.performance.now(),
                    points: activeStroke.map((point) => ({ ...point })),
                });
                laserTrail.activeStroke = [];
                return;
            }

            laserTrail.activeStroke = [];
        }

        function updateLaserRotateModifier(isPressed) {
            if (laserRotateModifierPressedRef.current === isPressed) {
                return;
            }

            laserRotateModifierPressedRef.current = isPressed;
            syncControlsInteractionBindings();
        }

        function appendLaserSample(localX, localY, now, options = {}) {
            const pointerState = laserPointerStateRef.current;
            const laserTrail = laserTrailRef.current;
            const { force = false } = options;
            const { lastSamplePosition } = pointerState;
            const sampleDistanceSquared = sampleMinDistance * sampleMinDistance;
            const deltaX = lastSamplePosition ? lastSamplePosition.x - localX : 0;
            const deltaY = lastSamplePosition ? lastSamplePosition.y - localY : 0;
            const shouldAddSample = force
                || !lastSamplePosition
                || ((deltaX * deltaX) + (deltaY * deltaY)) >= sampleDistanceSquared
                || (now - pointerState.lastSampleAt) >= sampleMinIntervalMs;

            if (!shouldAddSample) {
                return false;
            }

            const point = {
                x: localX,
                y: localY,
            };

            laserTrail.activeStroke.push(point);
            pointerState.lastSampleAt = now;
            pointerState.lastSamplePosition = point;

            return true;
        }

        function syncLaserPointerPosition(event) {
            if (interactionModeRef.current !== 'laser') {
                return;
            }

            const bounds = rendererDomElement.getBoundingClientRect();
            const localX = event.clientX - bounds.left;
            const localY = event.clientY - bounds.top;
            const isInside = localX >= 0
                && localX <= bounds.width
                && localY >= 0
                && localY <= bounds.height;

            if (!isInside) {
                laserPointerStateRef.current.inside = false;
                stopLaserTrail();
                hideLaserCursor(laserCursorRef.current);
                return;
            }

            laserPointerStateRef.current.inside = true;
            laserPointerStateRef.current.localX = localX;
            laserPointerStateRef.current.localY = localY;
            positionLaserCursor(laserCursorRef.current, localX, localY);
        }

        const trackMousePointerDown = (event) => {
            if (interactionModeRef.current === 'laser') {
                if (event.pointerType !== 'mouse') {
                    return;
                }

                if (event.button === 0 && !event.altKey) {
                    laserPointerStateRef.current.isPressed = true;
                    laserPointerStateRef.current.lastSampleAt = 0;
                    laserPointerStateRef.current.lastSamplePosition = null;
                    laserTrailRef.current.activeStroke = [];
                    syncLaserPointerPosition(event);
                    appendLaserSample(
                        laserPointerStateRef.current.localX,
                        laserPointerStateRef.current.localY,
                        window.performance.now(),
                        { force: true },
                    );

                    event.preventDefault();
                    return;
                }

                activeMousePointerIdRef.current = event.pointerId;
                return;
            }

            if (event.pointerType !== 'mouse') {
                return;
            }

            activeMousePointerIdRef.current = event.pointerId;
        };

        const clearTrackedMousePointer = (event) => {
            if (interactionModeRef.current === 'laser') {
                if (event.pointerType !== 'mouse') {
                    return;
                }

                if (event.button === 0 && laserPointerStateRef.current.isPressed) {
                    stopLaserTrail();
                    return;
                }

                if (activeMousePointerIdRef.current === event.pointerId) {
                    activeMousePointerIdRef.current = null;
                }

                return;
            }

            if (event.pointerType !== 'mouse') {
                return;
            }

            if (activeMousePointerIdRef.current === event.pointerId) {
                activeMousePointerIdRef.current = null;
            }
        };

        const flushMousePointerInteraction = () => {
            const activePointerId = activeMousePointerIdRef.current;

            if (activePointerId == null) {
                return;
            }

            activeMousePointerIdRef.current = null;

            try {
                if (rendererDomElement.hasPointerCapture?.(activePointerId)) {
                    rendererDomElement.releasePointerCapture(activePointerId);
                }
            } catch (error) {
                // Pointer capture may already be gone; dispatching pointerup is the real fallback.
            }

            if (typeof window.PointerEvent === 'function') {
                rendererDomElement.dispatchEvent(new window.PointerEvent('pointerup', {
                    bubbles: true,
                    button: -1,
                    buttons: 0,
                    cancelable: true,
                    clientX: 0,
                    clientY: 0,
                    isPrimary: true,
                    pointerId: activePointerId,
                    pointerType: 'mouse',
                }));

                return;
            }

            rendererDomElement.dispatchEvent(new window.MouseEvent('mouseup', {
                bubbles: true,
                button: 0,
                buttons: 0,
                cancelable: true,
                clientX: 0,
                clientY: 0,
            }));
        };

        flushMousePointerInteractionRef.current = flushMousePointerInteraction;

        const handleWindowPointerUp = (event) => {
            if (interactionModeRef.current === 'laser') {
                if (event.pointerType !== 'mouse') {
                    return;
                }

                if (event.button === 0 && laserPointerStateRef.current.isPressed) {
                    stopLaserTrail();
                    return;
                }

                if (activeMousePointerIdRef.current === event.pointerId) {
                    flushMousePointerInteraction();
                }

                return;
            }

            if (event.pointerType !== 'mouse') {
                return;
            }

            if (activeMousePointerIdRef.current !== event.pointerId) {
                return;
            }

            flushMousePointerInteraction();
        };

        const handleLaserPointerEnter = (event) => {
            syncLaserPointerPosition(event);
        };

        const handleLaserPointerMove = (event) => {
            syncLaserPointerPosition(event);

            if (interactionModeRef.current !== 'laser' || !laserPointerStateRef.current.isPressed) {
                return;
            }

            if (!laserPointerStateRef.current.inside) {
                return;
            }

            appendLaserSample(
                laserPointerStateRef.current.localX,
                laserPointerStateRef.current.localY,
                window.performance.now(),
            );
        };

        const handleLaserPointerLeave = () => {
            if (interactionModeRef.current !== 'laser') {
                return;
            }

            laserPointerStateRef.current.inside = false;
            stopLaserTrail();
            hideLaserCursor(laserCursorRef.current);
        };

        const handleLaserWheel = (event) => {
            if (interactionModeRef.current !== 'laser') {
                return;
            }

            event.preventDefault();
        };

        const handleLaserContextMenu = (event) => {
            if (interactionModeRef.current !== 'laser') {
                return;
            }

            event.preventDefault();
        };

        const handleWindowKeyDown = (event) => {
            if (event.key === 'Alt') {
                updateLaserRotateModifier(true);
            }
        };

        const handleWindowKeyUp = (event) => {
            if (event.key === 'Alt') {
                updateLaserRotateModifier(false);
            }
        };

        const handleWindowBlur = () => {
            updateLaserRotateModifier(false);

            if (interactionModeRef.current === 'laser' && laserPointerStateRef.current.isPressed) {
                stopLaserTrail();
            }

            flushMousePointerInteraction();
        };

        const updateLaserVisuals = () => {
            const laserCanvasContext = laserCanvasContextRef.current;
            const laserCanvas = laserCanvasRef.current;

            if (!laserCanvasContext || !laserCanvas) {
                return;
            }

            clearLaserCanvas(laserCanvasContextRef, laserCanvasRef);

            const now = window.performance.now();
            const pointerState = laserPointerStateRef.current;
            const laserTrail = laserTrailRef.current;
            const nextFadingStrokes = laserTrail.fadingStrokes.filter((stroke) => (
                Math.max(1 - ((now - stroke.fadeStartedAt) / fadeMs), 0) > 0
            ));

            if (nextFadingStrokes.length !== laserTrail.fadingStrokes.length) {
                laserTrail.fadingStrokes = nextFadingStrokes;
            }

            const hasActiveStroke = laserTrail.activeStroke.length > 0;
            const hasFadingStrokes = laserTrail.fadingStrokes.length > 0;

            if (interactionModeRef.current !== 'laser' && !hasActiveStroke && !hasFadingStrokes) {
                return;
            }

            laserTrail.fadingStrokes.forEach((stroke) => {
                const strokeOpacity = Math.max(1 - ((now - stroke.fadeStartedAt) / fadeMs), 0);

                if (stroke.points.length < 2 || strokeOpacity <= 0) {
                    return;
                }

                drawLaserStroke(laserCanvasContext, stroke.points, strokeOpacity);
            });

            if (!pointerState.isPressed) {
                return;
            }

            const strokePoints = laserTrail.activeStroke.slice();
            const lastStoredPoint = strokePoints[strokePoints.length - 1];

            if (
                pointerState.inside
                && (
                    !lastStoredPoint
                    || lastStoredPoint.x !== pointerState.localX
                    || lastStoredPoint.y !== pointerState.localY
                )
            ) {
                strokePoints.push({
                    x: pointerState.localX,
                    y: pointerState.localY,
                });
            }

            if (strokePoints.length < 2) {
                return;
            }

            drawLaserStroke(laserCanvasContext, strokePoints, 1);
        };

        const handleResize = () => {
            laserCanvasContextRef.current = resizeLaserCanvas(laserCanvasRef.current);
        };

        const animate = () => {
            updateLaserVisuals();
            frameIdRef.current = window.requestAnimationFrame(animate);
        };

        handleResize();

        if (window.ResizeObserver) {
            resizeObserver = new window.ResizeObserver(handleResize);
            if (laserCanvasRef.current) {
                resizeObserver.observe(laserCanvasRef.current);
            }
            resizeObserver.observe(rendererDomElement);
        }

        frameIdRef.current = window.requestAnimationFrame(animate);

        rendererDomElement.addEventListener('pointerdown', trackMousePointerDown);
        rendererDomElement.addEventListener('pointerenter', handleLaserPointerEnter);
        rendererDomElement.addEventListener('pointermove', handleLaserPointerMove);
        rendererDomElement.addEventListener('pointerleave', handleLaserPointerLeave);
        rendererDomElement.addEventListener('pointerup', clearTrackedMousePointer);
        rendererDomElement.addEventListener('pointercancel', clearTrackedMousePointer);
        rendererDomElement.addEventListener('lostpointercapture', flushMousePointerInteraction);
        rendererDomElement.addEventListener('contextmenu', handleLaserContextMenu);
        rendererDomElement.addEventListener('wheel', handleLaserWheel, { passive: false });
        window.addEventListener('pointerup', handleWindowPointerUp);
        window.addEventListener('pointercancel', flushMousePointerInteraction);
        window.addEventListener('keydown', handleWindowKeyDown);
        window.addEventListener('keyup', handleWindowKeyUp);
        window.addEventListener('blur', handleWindowBlur);
        window.addEventListener('resize', handleResize);

        return () => {
            if (frameIdRef.current) {
                window.cancelAnimationFrame(frameIdRef.current);
                frameIdRef.current = null;
            }

            rendererDomElement.removeEventListener('pointerdown', trackMousePointerDown);
            rendererDomElement.removeEventListener('pointerenter', handleLaserPointerEnter);
            rendererDomElement.removeEventListener('pointermove', handleLaserPointerMove);
            rendererDomElement.removeEventListener('pointerleave', handleLaserPointerLeave);
            rendererDomElement.removeEventListener('pointerup', clearTrackedMousePointer);
            rendererDomElement.removeEventListener('pointercancel', clearTrackedMousePointer);
            rendererDomElement.removeEventListener('lostpointercapture', flushMousePointerInteraction);
            rendererDomElement.removeEventListener('contextmenu', handleLaserContextMenu);
            rendererDomElement.removeEventListener('wheel', handleLaserWheel);
            window.removeEventListener('pointerup', handleWindowPointerUp);
            window.removeEventListener('pointercancel', flushMousePointerInteraction);
            window.removeEventListener('keydown', handleWindowKeyDown);
            window.removeEventListener('keyup', handleWindowKeyUp);
            window.removeEventListener('blur', handleWindowBlur);
            window.removeEventListener('resize', handleResize);
            resizeObserver?.disconnect();
            flushMousePointerInteractionRef.current = null;
            hideLaserCursor(laserCursorRef.current);
            clearLaserTrail(laserTrailRef, laserPointerStateRef);
            clearLaserCanvas(laserCanvasContextRef, laserCanvasRef);
            laserCanvasContextRef.current = null;
        };
    }, [controlsRef, fadeMs, rendererDomElement, sampleMinDistance, sampleMinIntervalMs]);

    return {
        cursorStyle: LASER_CURSOR_STYLE,
        laserCanvasRef,
        laserCursorRef,
        laserStyle: LASER_CANVAS_STYLE,
    };
}

export default useLaserOverlay;
